<template>
  <div class="sales-main-container">
    <CRow class="mb-2">
      <CCol md="6" xl="4">
        <div class="d-flex">
          <h1 class="main-header mr-3" v-translate>Sales report</h1>
          <PDropDown :items="reportOptions"
                     :show-item-selector="true"
                     :reset-on-select="false"
                     :font-weight="'font-weight-semi-bold'"
                     @change="allFreePaidDropdownClick"
                     :disabled="isLoadingSalesTotal"/>
        </div>
      </CCol>
      <CCol xl="auto">
        <div class="overflow-auto pb-2">
          <ul class="nav nav-pills chart-pills flex-nowrap">
            <li class="nav-item">
              <a class="nav-link text-capitalize" href="#"
                 :class="{'active': !selectedIndustry && salesTotal, 'disabled': !salesTotal || isLoadingSalesTotal}"
                 @click.prevent="selectIndustry()">
                <translate>All</translate>
              </a>
            </li>
            <li class="nav-item" v-for="(item, index) in industriesSelector" :key="index">
              <a class="nav-link text-capitalize" href="#"
                 :class="{'active': selectedIndustry===item,
                               'disabled': !salesTotal || isLoadingSalesTotal}"
                 @click.prevent="selectIndustry(item)">
                {{ getIndustryConfig(item).label }}
              </a>
            </li>
          </ul>
        </div>
      </CCol>
    </CRow>
    
    <CRow>
      <CCol class="mb-2">
        <div class="d-flex flex-row align-items-start">
          <div style="min-width: 240px" class="mr-2 mb-2">
            <div class="input-group input-group-sm input-group-date-time" :class="{'datepicker-disabled': isLoadingSalesTotal}">
              <VueCtkDateTimePicker
                  :value="selectedDateRange"
                  @validate="onSelectRange($event)"
                  @input="onDateInput($event)"
                  color="#1867FF"
                  :range="true"
                  :auto-close="true"
                  :no-button="false"
                  :input-size="`sm`"
                  :no-label="true"
                  :label="$gettext('Select a date range')"
                  format="YYYY-MM-DD"
                  formatted="L"
                  :shortcut="calendarDefaultShortcut"
                  :custom-shortcuts="datePickerShortcuts"
                  :locale="language"
                  :no-button-now="true"/>
              <div class="input-group-append">
                <span class="input-group-text"><CIcon name="cipCalendar"></CIcon></span>
              </div>
            </div>
            <div class="mt-1 ml-5 mr-4" style="font-size: 12px;font-style: italic">
              <translate>Time zone:</translate>
              {{ timeZone.EUROPE_ROME }}
            </div>
          </div>

          <div class="mb-2">
            <CInput block type="search" :value="filterPhotoServiceName" @input="setFilterPhotoServiceName($event.target.value)" class="input-group-clean"
                    @keyup.enter="filterByPhotoService($event)"
                    :placeholder="$gettext('Search')"
            >
              <template #append>
                <CButton @click="filterByPhotoService($event)">
                  <CIcon name="cip-search2" class="text-secondary" size="custom" :height="14"/>
                </CButton>
              </template>
            </CInput>
          </div>
        </div>
      </CCol>
    </CRow>
    
    <ViewLoadingMessage :loading="isLoadingSalesTotal"/>
    
    <template v-if="!isLoadingSalesTotal">
      <CRow class="my-2">
        <CCol md="auto" class="mb-2">
          <SalesReportWidgetHighlight title="Sales"
                                     :total="totalAmountEur|currency(currencyCodes.EUR)"
                                     :items="itemsB2C"
                                     :vat="$gettext('VAT incl.')"
                                     v-if="salesTotal"
                                     :variant="`blue`"
          />
        </CCol>
        
        <CCol md="auto" class="mb-2">
          <div class="weird-sep"></div>
        </CCol>
        
        <CCol md="auto" class="mb-2">
          <SalesReportWidgetHighlight :title="$gettext('SaaS')"
                                     :total="turnover|currency(currencyCodes.EUR)"
                                     :vat="$gettext('VAT excl.')"
                                     :variant="`green`"
                                     v-if="salesTotal"/>
        </CCol>

        <CCol md="auto" class="mb-2">
          <div class="weird-sep"></div>
        </CCol>
        
        <CCol md="auto" class="mb-2">
          <SalesReportWidgetHighlight :title="$gettext('Pre-sale')"
                                     :total="preSale|currency(currencyCodes.EUR)"
                                     :vat="$gettext('VAT excl.')"
                                     :variant="`yellow`"
                                     v-if="salesTotal"/>
        </CCol>
        
        <!-- SaaS Payments Total Widget -->
        <template v-if="hasPerm('admin.stats.saas_payments_total')">
          <CCol md="auto" class="mb-2" >
            <div class="weird-sep"></div>
          </CCol>

          <CCol md="auto" class="mb-2" >
            <SaasPaymentsWidget/>
          </CCol>
        </template>
      </CRow>

      <!-- Industries Cards -->
      <template v-if="salesTotal">
        <IndustryCard 
          v-for="(industry, index) in industries" 
          :key="index"
          :industry="industry" 
        />
      </template>

      <!-- SaaS Payments List -->
      <template v-if="hasPerm('admin.stats.saas_payments_list')">
        <SaasPaymentsList/>
      </template>
    </template>
  </div>
</template>

<script>
import ViewLoadingMessage from "@/components/ViewLoadingMessage.vue"
import SalesReportWidgetHighlight from "./components/SalesReportWidgetHighlight.vue"
import SaasPaymentsList from "./components/SaasPaymentsList.vue"
import SaasPaymentsWidget from "./components/SaasPaymentsWidget.vue"
import IndustryCard from "./components/IndustryCard.vue"
import PDropDown from "@/domain/core/components/PDropDown.vue"
import { mapGetters, mapActions } from "vuex"
import { currencyCodes } from "@/constants"
import { 
  datePickerShortcuts,
  industriesConfig
} from "@/views_pica/stats/constants"
import { reportOptions } from "./constants"
import { getAverage, getPercentage } from "@/utils/functions"
import { timeZone } from "@/utils/date"

export default {
  name: "SalesReport",
  
  components: {
    ViewLoadingMessage,
    SalesReportWidgetHighlight, 
    SaasPaymentsList,
    SaasPaymentsWidget,
    IndustryCard,
    PDropDown
  },
  
  data() {
    return {
      collapseDuration: 0,
      sorterValueEvents: null,
      eventsLoading: false,
      isLoadingSalesTotal: false
    }
  },
  
  computed: {
    ...mapGetters('salesReport', [
      'salesTotal',
      'allPhotoServicesByIndustry',
      'totalAmountEur',
      'totalHandlingFee', 
      'turnover',
      'preSale',
      'industries',
      'selectedDateRange',
      'selectedIndustry',
      'filterPhotoServiceName',
      'isPromo'
    ]),
    
    reportOptions() {
      return Object.values(reportOptions);
    },
    
    timeZone() {
      return timeZone
    },
    
    datePickerShortcuts() {
      return datePickerShortcuts
    },
    
    currencyCodes() {
      return currencyCodes
    },
    
    industriesConfig() {
      return industriesConfig
    },
    
    calendarDefaultShortcut() {
      return 'today'
    },
    
    industriesSelector() {
      return Object.keys(this.industriesConfig).filter(item => item !== 'stadium')
    },
    
    itemsB2C() {
      return !this.selectedIndustry ? [
        {
          label: this.$gettext('Fees'),
          value: this.$options.filters.currency(this.totalHandlingFee, this.currencyCodes.EUR),
        }
      ] : null
    }
  },
  
  async mounted() {
    await this.fetchConfig({type: 'admin'})
    if (!this.calendarDefaultShortcut) await this.getAllData()
  },
  
  methods: {
    ...mapActions('salesReport', [
      'fetchSalesTotal',
      'fetchPhotoServiceTotals',
      'fetchPhotoServiceEventsTotals',
      'setPhotoServicesByIndustry',
      'resetPhotoServicesByIndustry',
      'setSelectedDateRange',
      'setSelectedIndustry',
      'setFilterPhotoServiceName',
      'setIsPromo'
    ]),
    ...mapActions('login', [
      'fetchConfig'
    ]),
    
    getPercentage,
    getAverage,
    
    allFreePaidDropdownClick(val) {
      if (val === reportOptions.ALL.value) {
        this.setIsPromo(null)
      } else if (val === reportOptions.FREE.value) {
        this.setIsPromo(true)
      } else if (val === reportOptions.PAID.value) {
        this.setIsPromo(false)
      }
      this.getAllData()
    },
    
    onDateInput(dateRange) {
      if (!dateRange || (dateRange.start && dateRange.end) || (!dateRange.start && !dateRange.end)) {
        this.setSelectedDateRange(dateRange)
        this.getAllData()
      }
    },
    
    onSelectRange() {
      this.getAllData()
    },
    
    filterByPhotoService() {
      this.getAllData()
    },
    
    async getAllData() {
      this.resetPhotoServicesByIndustry()
      await this.loadSalesTotal()
    },
    
    async loadSalesTotal() {
      this.isLoadingSalesTotal = true
      try {
        await this.fetchSalesTotal()
      } catch (error) {
        console.error('Error loading sales total:', error)
      } finally {
        this.isLoadingSalesTotal = false
      }
    },
    
    
    selectIndustry(industry) {
      this.setSelectedIndustry(industry || null)
      this.getAllData()
    },
    
    getIndustryConfig(industry) {
      return this.industriesConfig[industry]
    }
  }
}
</script>

<style scoped lang="scss">
.weird-sep {
  width: 1px;
  height: 100px;
  background-color: #e4e7ea;
  margin: 0 1rem;
}

.chart-card--purple .card-header {
  background-color: #6f42c1;
  color: white;
}

.dot-purple::before {
  background-color: #6f42c1;
}
</style>